/**
 * Which left-rail pages a tenant should be offered.
 *
 * Mirrors what the backend actually allows (entity-access.middleware.ts), so a
 * user is never shown a link that only ends in a refusal:
 *  - insurance / shipper: everything.
 *  - broker: no shipper-intel pages, unless the company carries the explicit
 *    shipper-intel override (`shipper_intel_override`).
 *  - carrier: own-record pages only (the backend locks every carrier route to
 *    the company's own DOT, so market-wide pages are dead ends).
 * Super admins, and users whose company is unknown, see everything: hiding a
 * page from someone who may be entitled to it is worse than showing it.
 */

export type TenantType = 'insurance' | 'shipper' | 'broker' | 'carrier';

export interface TenantNavUser {
  role?: string;
  rbac_role?: string | null;
  entity_type?: string | null;
  shipper_intel_override?: boolean;
}

const SHIPPER_INTEL_ROUTES = ['/shipper-audit', '/dnu-analytics', '/carriers-by-shipper', '/tags-management'];

const CARRIER_HIDDEN_ROUTES = [
  ...SHIPPER_INTEL_ROUTES,
  '/carriers-by-location',
  '/carrier-compare',
  '/carrier-comparison',
  '/bulk-screening',
  '/market-intelligence',
  '/geographic',
  '/vin-matches',
  '/merge',
  '/statistics',
  '/carrier-watchlist',
];

const isTenantType = (v: unknown): v is TenantType =>
  v === 'insurance' || v === 'shipper' || v === 'broker' || v === 'carrier';

export function isSuperAdminUser(user: TenantNavUser | null | undefined): boolean {
  return user?.role === 'super_admin' || user?.rbac_role === 'super_admin';
}

/** Routes to hide from this user's rail. Empty means show everything. */
export function hiddenNavRoutes(user: TenantNavUser | null | undefined): string[] {
  if (!user || isSuperAdminUser(user) || !isTenantType(user.entity_type)) return [];
  switch (user.entity_type) {
    case 'broker':
      return user.shipper_intel_override ? [] : SHIPPER_INTEL_ROUTES;
    case 'carrier':
      return CARRIER_HIDDEN_ROUTES;
    default:
      return [];
  }
}
